import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, defaultIfEmpty, map } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/services/auth.service';
import { authRedirectTokenFor } from 'src/app/shared/Helper/auth-redirect';

/**
 * Requires a session for the asset deposit pages.
 *
 * Every "Deposit asset" entry point — header, both dashboards, the KYC completion card, the
 * redeem page, the 404 — routes to `/deposit-asset`, so gating the route covers all of them
 * at once rather than repeating a login check at each call site.
 *
 * The backend's final submit also refuses an anonymous deposit, because a draft with no owner
 * has no submission history to price the fee against and would be free every time. A guest can
 * still fill in the whole form before that check fires, and cannot carry the draft over once
 * they sign in — so the session is required up front, where nothing is lost.
 *
 * Sends the user to `/sign-in?redirect=deposit`; sign-in and sign-up both read that param and
 * return the user here once they authenticate.
 */
export const depositAuthGuard: CanActivateFn = (_route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const sendToSignIn = () => {
        const redirect = authRedirectTokenFor(state.url);
        return router.createUrlTree(['/sign-in'], {
            queryParams: redirect ? { redirect } : {}
        });
    };

    // Deliberately NOT a forced refresh. `getCurrentUser()` answers from the in-memory
    // session when there is one, so a signed-in user navigates instantly — the same way
    // every other route in the shell resolves, since `AuthService.canActivateChild`
    // already runs on this navigation with the cached user. Forcing a network probe here
    // put a blocking `/auth/me` round trip in front of the deposit CTA and nothing else,
    // which is why that one button felt dead: on a busy page the request queues, and the
    // click did nothing visible until it came back (or until the 20s timeout bounced the
    // user to sign-in). The cache is not the security boundary — the backend refuses an
    // anonymous deposit regardless, and a server-side 401 still clears the session and
    // redirects through the auth interceptor.
    return authService.getCurrentUser().pipe(
        // An interceptor can complete the session probe without emitting — a swallowed
        // 401 returns EMPTY — and a guard that emits no value leaves the router with no
        // verdict at all, so the navigation is dropped with no error and no URL change.
        // Treat "no answer" as "no session" so a click always resolves to something.
        defaultIfEmpty(null),
        map((user) => (user ? true : sendToSignIn())),
        catchError(() => of(sendToSignIn()))
    );
};
